import { mapProposalToCapitalGlobalStepPagamentoPrefill } from '@/features/proposals/components/capital-global/utils/map-proposal-to-step-pagamento-prefill.util';
import {
  capitalGlobalStep1ShouldShowFieldErrors,
  type CapitalGlobalStep1FieldApiLike,
} from '@/features/proposals/components/capital-global/utils/capital-global-step1-should-show-field-errors.util';
import type { CapitalGlobalProposalStep2FormApi } from '@/features/proposals/components/capital-global/steps/capital-global-proposal-step2-form.types';
import type { CapitalGlobalProposalStepProps } from '@/features/proposals/components/capital-global/capital-global-proposal-step.types';
import type { CapitalGlobalProposalStepPagamentoValues } from '@/features/proposals/schemas/capital-global/proposal-step-pagamento.schema';
import {
  getCapitalGlobalProposalPremiumPeriodicity,
  toPremiumPeriodicitySelectOptions,
} from '@/features/proposals/components/capital-global/steps/constants/capital-global-proposal-payment-step.constants';
import {
  useCapitalGlobalProposalInstallmentTypes,
} from '@/features/proposals/hooks/capital-global/use-capital-global-proposal-installment-types.hook';
import {
  getPaymentTypeByCode,
  requiresBankAccountForPayment,
  useCapitalGlobalProposalPaymentTypes,
} from '@/features/proposals/hooks/capital-global/use-capital-global-proposal-payment-types.hook';
import { proposalsService } from '@/services';
import type { PaymentInfoPayload } from '@/services/proposals/types';
import { toaster } from '@/shared/ui/toaster.component';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useRef, useState } from 'react';

const STEP2_BASE_VALIDATE_FIELD_NAMES = [
  'premiumPeriodicity',
  'paymentMethod',
  'bestDueDay',
] as const satisfies readonly (keyof CapitalGlobalProposalStepPagamentoValues)[];

const STEP2_BANK_VALIDATE_FIELD_NAMES = [
  'agency',
  'agencyDigit',
  'account',
  'accountDigit',
] as const satisfies readonly (keyof CapitalGlobalProposalStepPagamentoValues)[];

type Args = CapitalGlobalProposalStepProps & {
  form: CapitalGlobalProposalStep2FormApi;
};

function onlyDigits(value: string): string {
  return value.replace(/\D/g, '');
}

function resolveAnnualPremiumAmount(proposal: CapitalGlobalProposalStepProps['proposal']) {
  const value = proposal.duePremium ?? proposal.DuePremium;
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function resolveFirstDueDate(bestDueDay: number, minFirstDueDate?: string): string {
  const baseFromApi = minFirstDueDate
    ? DateTime.fromISO(minFirstDueDate).startOf('day')
    : null;
  const today = DateTime.now().startOf('day');
  const base = baseFromApi?.isValid && baseFromApi > today ? baseFromApi : today;

  const buildForMonth = (date: DateTime) => {
    const day = Math.min(bestDueDay, date.daysInMonth ?? 31);
    return date.set({ day }).startOf('day');
  };

  let candidate = buildForMonth(base);
  if (candidate < base) {
    candidate = buildForMonth(base.plus({ months: 1 }));
  }

  return candidate.toISODate() ?? '';
}

export function useCapitalGlobalProposalStep2ViewModel({
  form,
  proposal,
  policyId,
  isViewMode,
  onNext,
}: Args) {
  const submitAttemptedRef = useRef(false);
  const prefilledRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const queryClient = useQueryClient();
  const annualPremiumAmount = resolveAnnualPremiumAmount(proposal);
  const paymentTypesQuery = useCapitalGlobalProposalPaymentTypes();
  const installmentTypesQuery = useCapitalGlobalProposalInstallmentTypes();
  const { paymentTypes, paymentMethodOptions, isCatalogReady, isCatalogEmpty } =
    paymentTypesQuery;
  const {
    premiumPeriodicityOptions: premiumPeriodicityCatalog,
    isCatalogReady: isInstallmentCatalogReady,
    isCatalogEmpty: isInstallmentCatalogEmpty,
  } = installmentTypesQuery;
  const premiumPeriodicityOptions = toPremiumPeriodicitySelectOptions(
    premiumPeriodicityCatalog
  );
  const isPaymentStepReady =
    isCatalogReady &&
    !isCatalogEmpty &&
    isInstallmentCatalogReady &&
    !isInstallmentCatalogEmpty;

  useEffect(() => {
    if (paymentTypesQuery.isError) {
      toaster.create({
        title: 'Erro',
        description:
          paymentTypesQuery.error?.message ||
          'Erro ao carregar meios de pagamento.',
        type: 'error',
        duration: 5000,
      });
    }
  }, [paymentTypesQuery.isError, paymentTypesQuery.error]);

  useEffect(() => {
    if (installmentTypesQuery.isError) {
      toaster.create({
        title: 'Erro',
        description:
          installmentTypesQuery.error?.message ||
          'Erro ao carregar periodicidades do prêmio.',
        type: 'error',
        duration: 5000,
      });
    }
  }, [installmentTypesQuery.isError, installmentTypesQuery.error]);

  useEffect(() => {
    if (!proposal || prefilledRef.current || paymentTypesQuery.isLoading) return;
    if (installmentTypesQuery.isLoading) return;

    const prefill = mapProposalToCapitalGlobalStepPagamentoPrefill(
      proposal,
      paymentTypes
    );
    if (!prefill) return;

    prefilledRef.current = true;

    if (prefill.premiumPeriodicity) {
      form.setFieldValue('premiumPeriodicity', prefill.premiumPeriodicity);
    }
    if (prefill.paymentMethod) {
      form.setFieldValue('paymentMethod', prefill.paymentMethod);
    }
    if (prefill.bestDueDay) {
      form.setFieldValue('bestDueDay', prefill.bestDueDay);
    }
    if (prefill.agency !== undefined) {
      form.setFieldValue('agency', prefill.agency);
    }
    if (prefill.agencyDigit !== undefined) {
      form.setFieldValue('agencyDigit', prefill.agencyDigit);
    }
    if (prefill.account !== undefined) {
      form.setFieldValue('account', prefill.account);
    }
    if (prefill.accountDigit !== undefined) {
      form.setFieldValue('accountDigit', prefill.accountDigit);
    }
  }, [form, proposal, paymentTypes, paymentTypesQuery.isLoading, installmentTypesQuery.isLoading]);

  const shouldShowStep2FieldErrors = useCallback(
    (fieldApi: CapitalGlobalStep1FieldApiLike) => {
      return capitalGlobalStep1ShouldShowFieldErrors(
        isViewMode,
        submitAttemptedRef.current,
        fieldApi
      );
    },
    [isViewMode]
  );

  const buildPaymentPayload = useCallback((): PaymentInfoPayload | null => {
    const values = form.state.values;
    const periodicity = getCapitalGlobalProposalPremiumPeriodicity(
      values.premiumPeriodicity,
      premiumPeriodicityCatalog
    );
    const selectedPaymentType = getPaymentTypeByCode(
      values.paymentMethod,
      paymentTypes
    );
    const bestDueDay = Number(onlyDigits(values.bestDueDay));

    if (!periodicity || !selectedPaymentType || !Number.isInteger(bestDueDay)) {
      return null;
    }

    const payload: PaymentInfoPayload = {
      effectiveDate: proposal.effectiveDate,
      terminateDate: proposal.terminateDate,
      paymentInfo: {
        firstPaymentType: selectedPaymentType.code,
        firstInstallmentCode: periodicity.firstInstallmentCode,
        installmentCount: periodicity.installmentCount,
        dueDateFirstInstallment: resolveFirstDueDate(
          bestDueDay,
          proposal.minFirstDueDate
        ),
        dueDayOtherInstallments: bestDueDay,
      },
    };

    if (requiresBankAccountForPayment(values.paymentMethod, paymentTypes)) {
      if (selectedPaymentType.bankCode) {
        payload.paymentInfo.bankCode = selectedPaymentType.bankCode;
      }

      payload.paymentInfo.firstAccount = {
        agencyNumber: onlyDigits(values.agency),
        agencyDigit: onlyDigits(values.agencyDigit),
        accountNumber: onlyDigits(values.account),
        accountDigit: onlyDigits(values.accountDigit),
      };
    }

    return payload;
  }, [
    form,
    paymentTypes,
    premiumPeriodicityCatalog,
    proposal.effectiveDate,
    proposal.minFirstDueDate,
    proposal.terminateDate,
  ]);

  const handleSubmit = async () => {
    if (isViewMode) {
      onNext();
      return;
    }

    if (!isPaymentStepReady) {
      toaster.create({
        title: 'Aguarde',
        description:
          'Carregando opções de pagamento. Tente novamente em instantes.',
        type: 'warning',
        duration: 5000,
      });
      return;
    }

    if (isCatalogEmpty) {
      toaster.create({
        title: 'Erro',
        description:
          'Nenhum meio de pagamento disponível para Capital Global. Contate o suporte.',
        type: 'error',
        duration: 5000,
      });
      return;
    }

    if (isInstallmentCatalogEmpty) {
      toaster.create({
        title: 'Erro',
        description:
          'Nenhuma periodicidade de prêmio disponível para Capital Global. Contate o suporte.',
        type: 'error',
        duration: 5000,
      });
      return;
    }

    submitAttemptedRef.current = true;
    const requiresBankAccount = requiresBankAccountForPayment(
      form.state.values.paymentMethod,
      paymentTypes
    );
    const fieldsToValidate = [
      ...STEP2_BASE_VALIDATE_FIELD_NAMES,
      ...(requiresBankAccount ? STEP2_BANK_VALIDATE_FIELD_NAMES : []),
    ];

    const validationResults = await Promise.all(
      fieldsToValidate.map(name =>
        form.validateField(name as never, 'change')
      )
    );
    const hasErrors = validationResults.some(
      r => Array.isArray(r) && r.length > 0
    );
    if (hasErrors) return;

    if (!policyId.trim()) {
      toaster.create({
        title: 'Erro',
        description: 'ID da proposta não encontrado.',
        type: 'error',
        duration: 5000,
      });
      return;
    }

    if (!proposal.effectiveDate || !proposal.terminateDate) {
      toaster.create({
        title: 'Erro',
        description:
          'Datas de vigência não encontradas. Revise os dados do seguro antes de gerar a proposta.',
        type: 'error',
        duration: 5000,
      });
      return;
    }

    const payload = buildPaymentPayload();
    if (!payload) {
      toaster.create({
        title: 'Erro',
        description:
          'Não foi possível montar as informações de pagamento. Revise os campos preenchidos.',
        type: 'error',
        duration: 5000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const attachResult = await proposalsService.attachPaymentInfo(
        policyId,
        payload,
        proposal.productCode
      );

      if (!attachResult.success) {
        toaster.create({
          title: 'Erro ao anexar informações de pagamento',
          description:
            attachResult.error ||
            'Não foi possível anexar as informações de pagamento. Tente novamente.',
          type: 'error',
          duration: 5000,
        });
        return;
      }

      const calculateResult = await proposalsService.calculateProposal(
        policyId,
        proposal.productCode
      );

      if (!calculateResult.success) {
        toaster.create({
          title: 'Erro ao calcular proposta',
          description:
            calculateResult.error ||
            'Não foi possível calcular a proposta. Tente novamente.',
          type: 'error',
          duration: 5000,
        });
        return;
      }

      await queryClient.invalidateQueries({
        queryKey: ['proposal', policyId],
      });

      toaster.create({
        title: 'Sucesso',
        description: 'Informações de pagamento anexadas e proposta calculada.',
        type: 'success',
        duration: 3000,
      });

      onNext();
    } catch (error) {
      toaster.create({
        title: 'Erro',
        description:
          error instanceof Error
            ? error.message
            : 'Erro inesperado ao processar pagamento.',
        type: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    annualPremiumAmount,
    premiumPeriodicityOptions,
    premiumPeriodicityCatalog,
    paymentMethodOptions,
    paymentTypes,
    isLoadingPaymentTypes:
      paymentTypesQuery.isLoading || installmentTypesQuery.isLoading,
    isPaymentTypesReady: isPaymentStepReady,
    isSubmitting,
    shouldShowStep2FieldErrors,
    handleSubmit,
  };
}
